import { DeleteIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Card,
  Divider,
  HStack,
  IconButton,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getRecurrence, removeClientFromRecurrence } from 'services/api.service';
import { RecurrenceClient } from 'services/types/Recurrence.entity';
import { maskCurrency } from 'utils/number';
import AddClientToRecurrence from './components/AddClientToRecurrence';
import EditRecurrenceClient from './components/EditRecurrenceClient';
import LinkWalletToRecurrency from './components/LinkWalletToRecurrency';
import RealizarLevantamento from './components/RealizarLevantamento';
import { RecurrencyPeriodDescription } from './components/recurrency-type';
import ShowPaymentsRecurrence from './components/ShowPayments';

const getDateLabel = (value?: string | null, includeTime = false) => {
  if (!value) {
    return '---';
  }

  return DateTime.fromISO(value).toFormat(includeTime ? 'dd/MM/yyyy HH:mm:ss' : 'dd/MM/yyyy');
};

const getNextDateBadge = (value?: string | null) => {
  if (!value) {
    return <Badge colorScheme="gray">Sem agenda</Badge>;
  }

  const nextDate = DateTime.fromISO(value);

  if (nextDate < DateTime.now().startOf('day')) {
    return <Badge colorScheme="red">{nextDate.toFormat('dd/MM/yyyy')}</Badge>;
  }

  if (nextDate <= DateTime.now().plus({ days: 7 }).endOf('day')) {
    return <Badge colorScheme="yellow">{nextDate.toFormat('dd/MM/yyyy')}</Badge>;
  }

  return <Badge colorScheme="green">{nextDate.toFormat('dd/MM/yyyy')}</Badge>;
};

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  helper?: string;
}> = ({ label, value, helper }) => (
  <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4} bg="white">
    <Text fontSize="sm" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="2xl" fontWeight="semibold" color="gray.800">
      {value}
    </Text>
    {helper ? (
      <Text fontSize="sm" color="gray.500" mt={1}>
        {helper}
      </Text>
    ) : null}
  </Box>
);

const ShowClients: React.FC<{
  clients?: RecurrenceClient[];
  onClose: () => void;
  recurency: {
    id: string;
    name: string;
    amount: string;
  };
}> = ({ clients, onClose, recurency }) => {
  const { mutateAsync: removeCliente, isLoading: isRemovingCliente } = useMutation({
    mutationFn: (clientId: string) => removeClientFromRecurrence(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getRecurrence', recurency.id] });
      queryClient.invalidateQueries({ queryKey: ['findRecurrenceClientsSummary'] });
    },
  });

  if (!clients?.length) {
    return (
      <Box border="1px dashed" borderColor="gray.300" borderRadius="xl" px={6} py={10} textAlign="center">
        <Text fontSize="lg" fontWeight="semibold" color="gray.700">
          Nenhum pagador vinculado
        </Text>
        <Text color="gray.500" mt={2}>
          Adicione clientes para acompanhar a agenda, os valores pagos e os próximos vencimentos desta recorrência.
        </Text>
      </Box>
    );
  }

  return (
    <TableContainer border="1px solid" borderColor="gray.200" borderRadius="xl">
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            <Th>Cliente</Th>
            <Th>Período</Th>
            <Th isNumeric>Pendente</Th>
            <Th>Início</Th>
            <Th>Próxima cobrança</Th>
            <Th>Fim</Th>
            <Th isNumeric>Pago</Th>
            <Th>Ações</Th>
          </Tr>
        </Thead>
        <Tbody>
          {clients.map((item) => (
            <Tr key={item.id}>
              <Td fontWeight="medium">{item.client.name}</Td>
              <Td>{RecurrencyPeriodDescription[item.period]}</Td>
              <Td isNumeric>
                <Badge colorScheme={+item.totalPending > 0 ? 'red' : +item.totalPending === 0 ? 'green' : 'orange'}>
                  {maskCurrency(item.totalPending)}
                </Badge>
              </Td>
              <Td>{getDateLabel(item.initDate)}</Td>
              <Td>{getNextDateBadge(item.nextDate)}</Td>
              <Td>{getDateLabel(item.endDate)}</Td>
              <Td isNumeric>{maskCurrency(item.totalPaid)}</Td>
              <Td>
                <HStack spacing={2}>
                  <ShowPaymentsRecurrence
                    clientRecurrencyId={item.id}
                    recurrencyName={recurency.name}
                    amount={recurency.amount}
                    oncloseMainModal={onClose}
                  />
                  <EditRecurrenceClient recurrenceClient={item} recurrenceId={recurency.id} />
                  <IconButton
                    aria-label="Remover cliente"
                    icon={<DeleteIcon />}
                    colorScheme="red"
                    size="sm"
                    isLoading={isRemovingCliente}
                    onClick={() => {
                      removeCliente(item.id);
                    }}
                  />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default function ShowRecurrence() {
  const { id } = useParams<{ id: string }>();
  const { data, refetch } = useQuery(['getRecurrence', id], () => getRecurrence(id));

  const summary = useMemo(() => {
    const clients = data?.clients ?? [];
    const totalPending = clients.reduce((acc, client) => acc + Number(client.totalPending ?? 0), 0);
    const totalPaid = clients.reduce((acc, client) => acc + Number(client.totalPaid ?? 0), 0);
    const upcomingWeek = clients.filter((client) => {
      if (!client.nextDate) {
        return false;
      }

      const nextDate = DateTime.fromISO(client.nextDate);
      return nextDate >= DateTime.now().startOf('day') && nextDate <= DateTime.now().plus({ days: 7 }).endOf('day');
    }).length;
    const overdue = clients.filter((client) => {
      if (!client.nextDate) {
        return false;
      }

      return DateTime.fromISO(client.nextDate) < DateTime.now().startOf('day') && Number(client.totalPending) > 0;
    }).length;

    return {
      clients: clients.length,
      totalPending,
      totalPaid,
      upcomingWeek,
      overdue,
    };
  }, [data?.clients]);

  return (
    <Box>
      <SimpleGrid mb="20px" columns={{ sm: 1 }} spacing={{ base: '20px', xl: '20px' }}>
        <Card px={6} py={6}>
          <HStack justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" spacing={4}>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="3xl" fontWeight="bold" color="gray.800">
                {data?.description ?? 'Recorrência'}
              </Text>
              <Text color="gray.500">
                Recorrência #{data?.id} • {data?.period ? RecurrencyPeriodDescription[data.period] : 'Período não definido'}
              </Text>
            </VStack>
            <RealizarLevantamento recurrencyId={id} oncloseMainModal={refetch} />
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4} mt={6}>
            <SummaryCard label="Valor da recorrência" value={maskCurrency(data?.amount ?? 0)} />
            <SummaryCard label="Saldo disponível" value={maskCurrency(data?.balance ?? 0)} />
            <SummaryCard label="Pagadores ativos" value={summary.clients} helper={`${summary.upcomingWeek} vencem em 7 dias`} />
            <SummaryCard label="Total pendente" value={maskCurrency(summary.totalPending)} helper={`${summary.overdue} atrasados`} />
            <SummaryCard label="Total pago" value={maskCurrency(summary.totalPaid)} helper={data?.wallet?.length ? 'Carteira vinculada' : 'Carteira pendente'} />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mt={6}>
            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Status
              </Text>
              {data?.status ? <Badge colorScheme="green">Ativo</Badge> : <Badge colorScheme="red">Inativo</Badge>}
            </Box>
            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Criada em
              </Text>
              <Text fontWeight="medium">{getDateLabel(data?.createdAt, true)}</Text>
            </Box>
            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Carteira
              </Text>
              {data?.wallet?.length ? (
                <Badge colorScheme="green">Vinculada</Badge>
              ) : (
                <HStack spacing={3}>
                  <Badge colorScheme="red">Não vinculada</Badge>
                  <LinkWalletToRecurrency recurrencyId={id} oncloseMainModal={refetch} />
                </HStack>
              )}
            </Box>
            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={4}>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Tipo
              </Text>
              <Text fontWeight="medium">Recorrência automática</Text>
            </Box>
          </SimpleGrid>

          <Divider my={6} color="gray.300" />

          <VStack align="stretch" spacing={4}>
            <HStack justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
              <Box>
                <Text fontSize="xl" fontWeight="semibold" color="gray.800">
                  Pagadores
                </Text>
                <Text color="gray.500">
                  Gerencie clientes vinculados, acompanhe a saúde da carteira e acesse o histórico de cobranças.
                </Text>
              </Box>
              <AddClientToRecurrence recurrenceId={id} />
            </HStack>

            <ShowClients
              clients={data?.clients}
              onClose={refetch}
              recurency={{
                id,
                name: data?.description,
                amount: data?.amount,
              }}
            />
          </VStack>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
